/**
 * @fileoverview A styled pill component for displaying participant names or colors.
 *
 * This component renders a capsule-shaped pill that can display text or represent
 * colors. It includes special styling for winner states with gradients and
 * animations. The pill uses responsive sizing with em units and includes
 * backdrop blur effects for a modern glassy appearance.
 */

import React from 'react';
import '../../../src/screens/Frame/sections/MainContentSection/WinnerNameEffect.css';
import { APP_PROPERTIES } from '../../config/appProperties';
import { useTranslation } from 'react-i18next';

/**
 * Props for the CapsulePill component.
 */
interface CapsulePillProps {
  /** The background color of the pill. Can be a hex color code or any valid CSS color. */
  color?: string;
  /** The width of the pill. If not specified, it will auto-size based on content. */
  width?: number;
  /** The height of the pill in pixels. Defaults to 50px. */
  height?: number;
  /** The content to display inside the pill (typically a participant name). */
  children?: React.ReactNode;
  /** Flag indicating if this pill represents the winner, which applies special styling. */
  isWinner?: boolean;
  /** The text color of the pill. If not specified, it will be determined automatically. */
  textColor?: string;
  /** The color type for translation purposes */
  colorType?: 'random' | 'skin' | 'eyes';
  /** The color key for translation */
  colorKey?: string;
}

/**
 * A capsule-shaped pill component for displaying participant information.
 * 
 * This component creates a rounded pill that can display participant names
 * or represent colors. When marked as a winner, it applies special styling
 * including gradients, golden borders, and animations. The pill uses
 * responsive sizing with em units and includes modern glassy effects.
 * 
 * @param {CapsulePillProps} props - The props for the capsule pill component.
 * @returns {JSX.Element} The rendered capsule pill component.
 */
const CapsulePill: React.FC<CapsulePillProps> = ({
  color = APP_PROPERTIES.PILLS_COLOR,
  width,
  height = 50,
  children,
  isWinner = false,
  textColor,
  colorType,
  colorKey
}) => {
  const { t } = useTranslation();

  /**
   * Gets the translated text for the pill content
   */
  const getTranslatedText = (): string => {
    if (colorType && colorKey) {
      // Map colorType to the correct translation section
      let translationSection;
      switch (colorType) {
        case 'random':
          translationSection = 'colors';
          break;
        case 'skin':
          translationSection = 'skinColors';
          break;
        case 'eyes':
          translationSection = 'eyeColors';
          break;
        case 'hair':
          translationSection = 'hairColors';
          break;
        default:
          translationSection = 'colors';
      }
      // For color pills, use the translation system
      return t(`${translationSection}.${colorKey}`);
    }
    // For non-color pills, return the children as-is
    return typeof children === 'string' ? children : '';
  };

  // Use em units for pill sizing so it grows with font-size pulse and set base font size to 1.2em
  const emHeight = 1.8; // reduced from 2.2
  const emPaddingY = 0.4; // reduced from 0.5
  const emPaddingX = 0.7; // reduced from 1.2

  // Calculate responsive shadow sizes based on height
  const shadowBlur = Math.max(6, height * 0.25); // reduced from 8
  const shadowSpread = Math.max(3, height * 0.12); // reduced from 4
  const insetShadowBlur = Math.max(4, height * 0.2); // reduced from 6
  const insetShadowSpread = Math.max(2, height * 0.1); // reduced from 3

  // Check if the color is a hex color code
  const isColorCode = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(color);

  /**
   * Calculates the appropriate text color based on the background color for optimal contrast.
   * Uses luminance calculation to determine if white or black text will be more readable.
   * @param {string} backgroundColor - The background color (hex code)
   * @returns {string} The optimal text color ('white' or 'black')
   */
  const getContrastingTextColor = (backgroundColor: string): string => {
    // Handle non-hex colors by returning default
    if (!/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(backgroundColor)) {
      return APP_PROPERTIES.PILLS_TEXT_COLOR;
    }

    // Convert hex to RGB
    let hex = backgroundColor.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate relative luminance using the standard formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return white text for dark backgrounds, black text for light backgrounds
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  /**
   * Determines the background style based on whether the pill is a winner and if it's a color pill.
   * @returns {string} The CSS background value (solid color or gradient).
   */
  const getBackground = () => {
    if (!isWinner) return color;
    if (isColorCode) {
      // For color pills, use a gradient with the original color
      return `linear-gradient(145deg, ${color}99 0%, ${color} 40%, ${color} 70%, ${color}99 100%)`;
    }
    // For text pills, use the golden gradient
    return 'linear-gradient(145deg, #fffbe6 0%, #ffe066 40%, #ffd700 70%, #fff8dc 100%)';
  };

  /**
   * Determines the border color based on the winner state and color type.
   * @returns {string} The CSS border color value.
   */
  const getBorderColor = () => {
    if (!isWinner) return '#e0e0e0';
    if (isColorCode) return color;
    return '#ffd700';
  };

  return (
    <div
      className={isWinner ? 'winner-name-effect' : ''}
      style={{
        width: width || 'auto',
        minWidth: `${1.6 * emHeight}em`, // reduced from 1.8
        height: `${emHeight}em`,
        borderRadius: '9999px',
        display: 'inline-flex',
        overflow: isWinner ? 'visible' : 'hidden',
        boxShadow: isWinner 
          ? undefined
          : `-1px ${shadowBlur}px ${shadowSpread}px -10px rgba(0,0,0,0.3), 
             -${insetShadowBlur}px -${insetShadowBlur}px ${insetShadowSpread}px ${insetShadowSpread}px rgba(0,0,0,0.1) inset`,
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        fontWeight: 'bold',
        fontSize: '1.1em', // reduced from 1.2em
        color: textColor || (isColorCode ? getContrastingTextColor(color) : APP_PROPERTIES.PILLS_TEXT_COLOR),
        border: isWinner ? `2px solid ${getBorderColor()}` : '1px solid #e0e0e0',
        minHeight: 0,
        background: getBackground(),
        opacity: 0.8,
        transformOrigin: 'center center',
        zIndex: isWinner ? 10 : 1,
        paddingLeft: `${emPaddingX}em`,
        paddingRight: `${emPaddingX}em`,
        paddingTop: `${emPaddingY}em`,
        paddingBottom: `${emPaddingY}em`,
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
    >
      <span style={{
        textAlign: 'center',
        zIndex: 1,
        whiteSpace: 'nowrap',
        paddingLeft: 4,
        paddingRight: 4,
        fontSize: 'inherit',
      }}>{getTranslatedText()}</span>
    </div>
  );
};

export default CapsulePill; 